// src/pages/FollowUpPage.js
import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Chip,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  HourglassBottom as HourglassIcon,
  PendingActions as PendingIcon,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import DoneIcon from "@mui/icons-material/Done";
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useNavigate } from "react-router-dom";
import UnifiedPageWrapper from "../components/Layout/UnifiedPageWrapper";
import SmartTable from "../components/Layout/SmartTable";
import API from "../utils/api";
import { useGlobalCounts } from "../context/GlobalCountContext";
import { useToast } from "../context/GlobalToastContext";
import { tSuccess, tError } from "../utils/toast";
import ConfirmDialog from "../components/ConfirmDialog";
import useAuth from "../hooks/useAuth";
import CrmDialog from "../components/CrmDialog";
import AddFollowUpModal from "../components/AddFollowUpModal";
import ModalFooter from "../components/ModalFooter";
import ModalHeader from "../components/ModalHeader";
import { MenuItem } from "@mui/material";





export default function FollowUpPage() {
  const navigate = useNavigate();
  const { can } = useAuth();

  const [followups, setFollowups] = useState([]);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { refreshCounts } = useGlobalCounts();
  const { notifySuccess, notifyError } = useToast();
  const [openAdd, setOpenAdd] = useState(false);
 
  

  // ------------------------------
  // FETCH FOLLOWUPS
  // ------------------------------
  const fetchFollowUps = async () => {
    try {
      const res = await API.get("/followups/");
      await refreshCounts();
      console.log("✅ /followups GET:", res.data);
      setFollowups(
  res.data.map(f => {
    const due = f.due_date ? new Date(f.due_date) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let mobile_status = "In Progress";
    if (f.completed) mobile_status = "Completed";
    else if (due && due < today) mobile_status = "Overdue";

    return { ...f, mobile_status };
  })
);


    } catch (err) {
      console.error("Error fetching followups:", err);
    }
  };

  useEffect(() => {
    fetchFollowUps();
  }, []);

  // ------------------------------
  // HANDLE SAVE (UPDATE FOLLOW-UP)
  // ------------------------------
  const handleSaveFollowUp = async () => {
    if (!selectedFollowUp) return;

    try {
      await API.put(`/followups/${selectedFollowUp.id}/`, selectedFollowUp);
      await refreshCounts();
      tSuccess("Follow-up updated!");
      setOpenEdit(false);
      setSelectedFollowUp(null);
      fetchFollowUps(); // ✅ refresh table
    } catch (err) {
      tError("Failed to update follow-up.");
    }
  };

  const handleMarkCompleted = async (id) => {
    try {
      await API.patch(`/followups/${id}/`, { completed: true });
      await fetchFollowUps();
      await refreshCounts();
      notifySuccess("Follow-up marked as completed!");
    } catch (err) {
      notifyError("Error updating follow-up.");
    }
  };

  // ------------------------------
  // HANDLE DELETE FOLLOW-UP
  // ------------------------------
  const handleDeleteFollowUp = () => {
    if (!selectedFollowUp) return;
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await API.delete(`/followups/${selectedFollowUp.id}/`);
      await refreshCounts();
      tSuccess("Follow-up deleted!");
      setOpenEdit(false);
      setSelectedFollowUp(null);
      fetchFollowUps(); // refresh table
    } catch (err) {
      console.error("Delete error:", err);
      tError("Failed to delete follow-up.");
    }
  };

  // ------------------------------
  // TABLE CONFIG
  // ------------------------------
  const columns = [


    {
      field: "lead",
      headerName: "Lead",
      flex: 1.2,
      renderCell: (params) => {
        const { lead_name, lead } = params.row || {};
        return lead_name || (lead ? `Lead #${lead}` : "—");
      },
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 1.2,
      renderCell: (params) => params.row?.notes || "—",
    },
    {
      field: "due_date",
      headerName: "Due Date",
      flex: 0.9,
      renderCell: (params) => {
        const { due_date } = params.row || {};
        if (!due_date) return "—";
        const [yyyy, mm, dd] = due_date.split("-"); 
return `${mm}/${dd}/${yyyy}`;

      },
    },
	 {
      field: "mobile_status",
      headerName: "Status",
      hide: true,
      flex: 0.9,
      valueGetter: (params) => {
        const row = params.row || {};
        if (row.completed) return "Completed";
        const due = row.due_date ? new Date(row.due_date) : null;
        const today = new Date(); today.setHours(0,0,0,0);
        if (due && due < today) return "Overdue";
        return "In Progress";
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.9,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const isDone = Boolean(row?.completed);
        const due = row?.due_date ? new Date(row.due_date) : null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // ✅ Completed Case
        if (isDone) {
          return (
            <Chip
              icon={<DoneIcon sx={{ color: "green !important" }} />}
              label="Completed"
              sx={{
                color: "grey",
                backgroundColor: "rgba(158, 158, 158, 0.12)",
                fontWeight: 600,
		borderRadius: "6px",
              }}
            />
          );
        }

        // ✅ Overdue Case
        if (due && due < today) {
          return (
            <Chip
              icon={<PendingIcon sx={{ color: "#B01E1E !important" }} />}
              label="Overdue"
              className="pulse-overdue"
              sx={{
                color: "#B01E1E",
                backgroundColor: "rgba(244, 67, 54, 0.15)",
                fontWeight: 600,
		borderRadius: "6px",
                border: "1px solid rgba(244, 67, 54, 0.4)",
              }}
            />
          );
        }

        // ✅ In Progress (default)
        return (
          <Chip
            icon={<AutorenewIcon sx={{ color: "#1b53cd !important" }} />}
            label="In Progress"
            sx={{
              color: "#1b53cd",
              backgroundColor: "rgba(158, 158, 158, 0.1)",
              fontWeight: 600,
	      borderRadius: "6px",
            }}
          />
        );
      },
    },
    {
      field: "created_by",
      headerName: "Created By",
      flex: 1,
      valueGetter: (params) => {
        const { created_by_username, created_by_name, created_by } = params.row || {};
        return created_by_username || created_by_name || (created_by ? `User #${created_by}` : "—");
      },
      renderCell: (params) => {
        const { created_by_username, created_by_name } = params.row || {};
        return created_by_username || created_by_name || "—";
      },
    },
  ];

  // ------------------------------
  // ROW CLICK → OPEN MODAL
  // ------------------------------
  const handleRowClick = (params) => {
    setSelectedFollowUp(params.row);
    setOpenEdit(true);
  };

  // ------------------------------
  // RENDER
  // ------------------------------
  const actionButton = (
    <Button
      variant="contained"
      color="primary"
      startIcon={<AddIcon />}
      onClick={() => setOpenAdd(true)}
    >
      Add Follow-Up
    </Button>
  );

  return (
    <UnifiedPageWrapper
      title="Follow Ups"
      subtitle="Track and manage follow-up activities with leads. Schedule calls, meetings, and other interactions to maintain relationships and close deals."
      actionButton={actionButton}
    >
      <SmartTable
        rows={followups}
        columns={columns}
        onRowClick={handleRowClick}
mobileEssentialFields={[
    "lead_name",
    "notes",
    "due_date",
      "mobile_status",
    "created_by"
  ]}
      />
<AddFollowUpModal
  open={openAdd}
  onClose={() => setOpenAdd(false)}
  onCreated={fetchFollowUps}
/>



      {/* 🧩 ---------------------------------------------------------------------Edit Follow-Up Modal */}
      <CrmDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        PaperProps={{
          sx: {
            minHeight: "40vh",
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <ModalHeader
    title="Edit Follow-Up"
    subtitle="Update notes, due date, or status for this follow-up."
  />
        <DialogContent dividers sx={{ flex: 1 }}>
          {selectedFollowUp && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Lead"
                value={selectedFollowUp.lead_name || ""}
                disabled
                fullWidth
              />
              <TextField
                label="Due Date"
                type="date"
                value={
                  selectedFollowUp.due_date
                    ? selectedFollowUp.due_date.split("T")[0]
                    : ""
                }
                onChange={(e) =>
                  setSelectedFollowUp({
                    ...selectedFollowUp,
                    due_date: e.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Notes"
                value={selectedFollowUp.notes || ""}
                onChange={(e) =>
                  setSelectedFollowUp({
                    ...selectedFollowUp,
                    notes: e.target.value,
                  })
                }
                multiline
                rows={4}
                fullWidth
              />
              <TextField
  select
  label="Status"
  value={selectedFollowUp.completed ? "Completed" : "In Progress"}
  onChange={(e) =>
    setSelectedFollowUp({
      ...selectedFollowUp,
      completed: e.target.value === "Completed",
    })
  }
  InputLabelProps={{ shrink: true }}
  fullWidth
>
  <MenuItem value="In Progress">In Progress</MenuItem>
  <MenuItem value="Completed">Completed</MenuItem>
</TextField>

            </Box>
          )}
        </DialogContent>
 <ModalFooter
  showDelete={!!selectedFollowUp?.id && can.canDelete}
  deleteLabel="Delete"
  onDelete={handleDeleteFollowUp}
  onCancel={() => setOpenEdit(false)}
  saveLabel="Update"
  onSave={handleSaveFollowUp}
/>


      </CrmDialog>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Follow-Up"
        message="Are you sure you want to delete this follow-up? This action cannot be undone."
        itemName={selectedFollowUp?.lead_name ? `Follow-up for ${selectedFollowUp.lead_name}` : null}
      />
    </UnifiedPageWrapper>
  );
}